import { Box, GlobalStyles } from '@mui/material';
import React, { memo, useState } from 'react';

import { ensureListing } from '../../../util/data';
import ListingCard from '../../BasicUI/ListingCard/ListingCard';

// Orientation helpers. Each entry positions the caret on the card edge that
// faces the pin — Mapbox picks the anchor, we render the caret to match.
// `none` hides the caret so a Mapbox Popup's own tip isn't doubled-up on.
const CARET_POSITION_BY_ANCHOR = {
  top: { top: '-3px', left: '50%', marginLeft: '-3px' },
  bottom: { bottom: '-3px', left: '50%', marginLeft: '-3px' },
  left: { left: '-3px', top: '50%', marginTop: '-3px' },
  right: { right: '-3px', top: '50%', marginTop: '-3px' },
  'top-left': { top: '16px', left: '-3px' },
  'top-right': { top: '16px', right: '-3px' },
  'bottom-left': { bottom: '16px', left: '-3px' },
  'bottom-right': { bottom: '16px', right: '-3px' },
  none: { display: 'none' },
};

// .root from SearchMapInfoCard.module.css — applied only when no
// rootClassName override is passed.
const rootSx = (theme) => ({
  display: 'block',
  position: 'relative',
  height: 'auto',
  border: 0,
  padding: 0,
  borderRadius: '4px',
  boxShadow: theme.tokens.boxShadowPopupLight,
  transition: `all ${theme.tokens.transitionStyleButton}, box-shadow 0`,
  '&:hover': {
    cursor: 'pointer',
    boxShadow: theme.tokens.boxShadowPopup,
  },
});

const paginationInfoSx = (theme) => ({
  backgroundColor: theme.tokens.marketplaceColor,
  color: theme.tokens.matterColorLight,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'default',
  // .borderRadiusInheritBottom (composed in the original via classNames)
  borderBottomRightRadius: 'inherit',
  borderBottomLeftRadius: 'inherit',
});

const paginationPageSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightSemiBold,
  marginTop: 0,
  marginBottom: 0,
});

const paginationPrevSx = (theme) => ({
  display: 'inline-block',
  width: '36px',
  height: '40px',
  border: 0,
  backgroundImage:
    'url(\'data:image/svg+xml;utf8,<svg width="7" height="10" viewBox="0 0 7 10" xmlns="http://www.w3.org/2000/svg"><path d="M5.667 1c.094 0 .188.04.254.118.12.14.102.35-.038.47L1.85 5l4.032 3.412c.14.12.158.33.04.47-.12.14-.33.158-.47.04L1.117 5.254C1.043 5.19 1 5.098 1 5s.043-.19.118-.255L5.45 1.08c.064-.054.14-.08.217-.08" stroke="%23FFF" fill="%23FFF" fill-rule="evenodd"/></svg>\')',
  backgroundPosition: 'center',
  borderBottomLeftRadius: 'inherit',
  cursor: 'pointer',
  opacity: 0.5,
  transition: theme.tokens.transitionStyleButton,
  '&:hover': {
    opacity: 1,
  },
});

const paginationNextSx = (theme) => ({
  display: 'inline-block',
  width: '36px',
  height: '40px',
  border: 0,
  backgroundImage:
    'url(\'data:image/svg+xml;utf8,<svg width="7" height="10" viewBox="0 0 7 10" xmlns="http://www.w3.org/2000/svg"><path d="M1.333 9c-.094 0-.188-.04-.254-.118-.12-.14-.102-.35.038-.47L5.15 5 1.118 1.588c-.14-.12-.158-.33-.04-.47.12-.14.33-.158.47-.04l4.334 3.667C5.957 4.81 6 4.902 6 5s-.043.19-.118.255L1.55 8.92c-.064.054-.14.08-.217.08" stroke="%23FFF" fill="%23FFF" fill-rule="evenodd"/></svg>\')',
  backgroundPosition: 'center',
  borderBottomRightRadius: 'inherit',
  cursor: 'pointer',
  opacity: 0.5,
  transition: theme.tokens.transitionStyleButton,
  '&:hover': {
    opacity: 1,
  },
});

const caretShadowSx = (anchor) => (theme) => ({
  width: '6px',
  height: '6px',
  position: 'absolute',
  transform: 'rotate(45deg)',
  // Caret should have same box-shadow as label
  boxShadow: theme.tokens.boxShadowPopupLight,
  ...(CARET_POSITION_BY_ANCHOR[anchor] || CARET_POSITION_BY_ANCHOR.bottom),
});

const caretSx = (anchor, withCarousel) => (theme) => ({
  width: '6px',
  height: '6px',
  position: 'absolute',
  transform: 'rotate(45deg)',
  // Caret should have same bg-color and border as label
  backgroundColor: theme.tokens.matterColorLight,
  borderRightStyle: 'solid',
  borderRightColor: theme.tokens.matterColorNegative,
  borderRightWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: theme.tokens.matterColorNegative,
  borderBottomWidth: '1px',
  ...(CARET_POSITION_BY_ANCHOR[anchor] || CARET_POSITION_BY_ANCHOR.bottom),
  ...(withCarousel
    ? {
        backgroundColor: theme.tokens.marketplaceColor,
        borderRightColor: theme.tokens.marketplaceColor,
        borderBottomColor: theme.tokens.marketplaceColor,
      }
    : {}),
});

// :global popup rules from the deleted CSS module. These style the Mapbox
// Popup container/tip — parent DOM nodes NOT rendered by this component —
// so they live as global rules rather than element sx. Injected via
// <GlobalStyles> so they keep working after the .module.css is removed.
const popupGlobalStyles = {
  // Strip the Popup's default white padding/border/shadow so we don't get a
  // second container stacked around the card. The Popup's own tip remains
  // the only arrow shown.
  '.search-map-info-card-popup .mapboxgl-popup-content, .route-planner-info-card-popup .mapboxgl-popup-content':
    {
      padding: 0,
      background: 'transparent',
      boxShadow: 'none',
      borderRadius: 0,
    },
  // Keep the popup above other map markers while the card is open.
  '.search-map-info-card-popup, .route-planner-info-card-popup': {
    zIndex: 3,
  },
};

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Array} props.listings
 * @param {Function} props.onListingInfoCardClicked
 * @param {Function} props.createURLToListing
 * @param {boolean} props.annualPassIsValid
 * @param {string} props.urlCategory
 * @param {string} props.anchor
 */
const SearchMapInfoCardComponent = ({
  className = null,
  rootClassName = null,
  listings,
  onListingInfoCardClicked,
  createURLToListing,
  annualPassIsValid,
  urlCategory,
  anchor,
}) => {
  const [currentListingIndex, setCurrentListingIndex] = useState(0);

  const currentListing = ensureListing(listings[currentListingIndex]);
  const hasCarousel = listings.length > 1;
  const pagination = hasCarousel ? (
    <Box component="div" sx={paginationInfoSx}>
      <Box
        component="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setCurrentListingIndex(
            (prevIndex) => (prevIndex + listings.length - 1) % listings.length
          );
        }}
        sx={paginationPrevSx}
      />
      <Box component="div" sx={paginationPageSx}>{`${
        currentListingIndex + 1
      }/${listings.length}`}</Box>
      <Box
        component="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setCurrentListingIndex(
            (prevIndex) => (prevIndex + listings.length + 1) % listings.length
          );
        }}
        sx={paginationNextSx}
      />
    </Box>
  ) : null;

  // Reproduce classNames(rootClassName || css.root, className): the component's own
  // base style applies ONLY when no rootClassName override is passed; className is
  // always appended.
  const classes = [rootClassName, className].filter(Boolean).join(' ') || undefined;
  const rootSxOrNull = rootClassName ? null : rootSx;
  const withCarousel = hasCarousel && (anchor === 'bottom' || !anchor);

  return (
    <Box className={classes} component="div" sx={rootSxOrNull}>
      <GlobalStyles styles={popupGlobalStyles} />
      <Box component="div" sx={caretShadowSx(anchor)} />
      <ListingCard
        addHelmet={false}
        annualPassIsValid={annualPassIsValid}
        createURLToListing={createURLToListing}
        disableBlur={true}
        listing={currentListing}
        onListingInfoCardClicked={onListingInfoCardClicked}
        urlCategory={urlCategory}
      />
      {pagination}
      <Box component="div" sx={caretSx(anchor, withCarousel)} />
    </Box>
  );
};

const SearchMapInfoCard = memo(SearchMapInfoCardComponent);

export default SearchMapInfoCard;
